import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

interface Props<T> {
  items: T[];
  getKey: (item: T) => string;
  /** copy: 0 för originalet, 1… för kopiorna som fyller ut loopen. */
  renderItem: (item: T, copy: number) => ReactNode;
  /** Hastighet: sekunder per objekt i loopen */
  secondsPerItem?: number;
  label: string;
  /** Rubrik ovanför raden. Paus-knappen hamnar då på samma rad som rubriken. */
  title?: string;
}

/**
 * Rullande rad som loopar. Pausar vid hovring och när man håller fingret på den,
 * och har en paus-knapp (WCAG 2.2.2: rörligt innehåll ska gå att stoppa).
 * Med färre än 3 objekt, eller om användaren har valt minskad rörelse, står raden still och går att scrolla.
 *
 * Raden upprepas så många gånger att den alltid fyller bredden, så att loopen
 * aldrig visar ett tomt glapp på breda skärmar.
 *
 * Med en rubrik (title) ligger paus-knappen till höger om rubriken, i stället för
 * på en egen rad under bilderna där den såg bortglömd ut.
 */
export function Ticker<T>({ items, getKey, renderItem, secondsPerItem = 6, label, title }: Props<T>) {
  const reduced = usePrefersReducedMotion();
  const [hoverPaused, setHoverPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [reps, setReps] = useState(2);
  const viewportRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const animate = items.length >= 3 && !reduced;

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const group = groupRef.current;
    if (!animate || !viewport || !group) return;
    const measure = () => {
      const g = group.offsetWidth;
      if (g > 0) setReps(Math.max(2, Math.ceil(viewport.clientWidth / g) + 1));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    ro.observe(group);
    return () => ro.disconnect();
  }, [animate, items.length]);

  if (items.length === 0) return null;

  const className = ['cn-ticker', animate && 'is-animated', (hoverPaused || userPaused) && 'is-paused'].filter(Boolean).join(' ');
  const group = (copy: number) => (
    <div className="cn-ticker__group" key={copy} ref={copy === 0 ? groupRef : undefined} aria-hidden={copy > 0 ? true : undefined}>
      {items.map((item) => (
        <div className="cn-ticker__item" key={getKey(item)}>{renderItem(item, copy)}</div>
      ))}
    </div>
  );

  const toggle = animate && (
    <button
      type="button"
      className="cn-ticker__toggle"
      onClick={() => setUserPaused((p) => !p)}
      aria-pressed={userPaused}
      aria-label={userPaused ? `Starta ${label.toLowerCase()}` : `Pausa ${label.toLowerCase()}`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        {userPaused ? <path d="M7 4.5v15l13-7.5z" /> : <path d="M6 4h4v16H6zM14 4h4v16h-4z" />}
      </svg>
    </button>
  );

  const viewport = (
    <div
      ref={viewportRef}
      className={className}
      role="region"
      aria-label={label}
      onTouchStart={() => setHoverPaused(true)}
      onTouchEnd={() => setHoverPaused(false)}
      onTouchCancel={() => setHoverPaused(false)}
    >
      <div
        className="cn-ticker__track"
        style={{ '--cn-duration': `${items.length * secondsPerItem}s`, '--cn-shift': `${-100 / reps}%` } as CSSProperties}
      >
        {Array.from({ length: animate ? reps : 1 }, (_, i) => group(i))}
      </div>
      {!title && toggle}
    </div>
  );

  if (!title) return viewport;
  return (
    <>
      <div className="cn-ticker__head cn-gutter">
        <h2 className="cn-h2">{title}</h2>
        {toggle}
      </div>
      {viewport}
    </>
  );
}
